import { getCSRFToken } from './csrf.js';

function debounce(func, delay) {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), delay);
  };
}

export function searchComponent(el) {
  return {
    query: '',
    results: [],
    open: false,
    exact_match: false,
    multiple: el.dataset.multiple === 'true', // Default to true for backward compatibility
    selected: el.dataset.value ? el.dataset.value.split(',').filter(v => v.trim() !== '') : [],
    selectedLabels: {},
    url: el.dataset.url,
    create: el.dataset.create,
    loading: false,
    el:el,
    isSelected(id) {
      return this.selected.includes(id.toString());
    },
    toggleSelection(id, name) {
      id = id.toString();
      if (this.isSelected(id)) {
        this.selected = this.selected.filter(item => item !== id);
        delete this.selectedLabels[id];
      } else {
        if (!this.multiple) {
          // In single-select mode, replace the current selection
          this.selected = [id];
          this.selectedLabels = {};
          this.selectedLabels[id] = name;
        } else {
          // In multi-select mode, add to the current selection
          this.selected = [...this.selected, id];
          this.selectedLabels[id] = name;
        }
      }
    },
    async submit() {
      this.loading = true;
      try {
        const res = await fetch(this.url, {
          method: 'POST',
          headers: {
            'X-CSRFToken': getCSRFToken(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ name: this.query })
        });

        if (!res.ok) throw new Error('Failed to submit data.');

        const data = await res.json();
        // Add to selection and update label
        this.toggleSelection(data.id, data.name);
        this.query = '';
        this.open = false;

      } catch (err) {
        console.error('Error:', err.message);
      } finally {
        this.loading = false;
      }
    },
    async fetchResults() {
      try {
        const res = await fetch(this.url +"?search="+ encodeURIComponent(this.query));
        if (!res.ok) throw new Error('Failed to fetch results.');

        const response = await res.json()
        this.results = response.results
        this.exact_match = this.results.some(result => result.name === this.query);
      } catch (err) {
        console.error('Error:', err.message);
        this.results = [];
      }
    },
    init() {
      // Initialize selectedLabels with existing values if available
      if (this.selected.length > 0) {
        // If we have labels from data-label attribute
        if (el.dataset.label) {
          const labels = el.dataset.label.split(',');
          // If we have the same number of labels as selected items, use them
          if (labels.length === this.selected.length) {
            for (let i = 0; i < this.selected.length; i++) {
              this.selectedLabels[this.selected[i]] = labels[i];
            }
          } else {
            // If the counts don't match, fetch labels
            this.fetchInitialLabels();
          }
        }
        // If we need to fetch labels for initial values
        else {
          this.fetchInitialLabels();
        }

        // In single-select mode, ensure only one item is selected
        if (!this.multiple && this.selected.length > 1) {
          const firstId = this.selected[0];
          this.selected = [firstId];
          const firstLabel = this.selectedLabels[firstId];
          if (firstLabel) {
            const tempLabel = this.selectedLabels[firstId];
            this.selectedLabels = {};
            this.selectedLabels[firstId] = tempLabel;
          }
        }

        // Clear the query field when there are selected items
        this.query = '';
      }

      this.$watch('query', debounce(() => {
        if (this.query.trim() !== '') {
          this.fetchResults();
        } else {
          this.results = [];
          this.exact_match = false;
        }
      }, 300));

      this.$watch('selected', () => {
        this.$nextTick(() => {
          const select = this.el.querySelector('select');
          if (select) {
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    },

    async fetchInitialLabels() {
      if (this.selected.length === 0) return;

      try {
        const ids = this.selected.join(',');
        const res = await fetch(`${this.url}?ids=${ids}`);
        if (!res.ok) throw new Error('Failed to fetch initial labels.');

        const items = await res.json();
        items.results.forEach(item => {
          this.selectedLabels[item.id] = item.name;
        });
      } catch (err) {
        console.error('Error fetching initial labels:', err.message);
      }
    }
  };
}

export function productComponent(el) {
  return {
    query: '',
    results: [],
    open: false,
    exact_match: false,
    multiple: el.dataset.multiple === 'true', // Default to true for backward compatibility
    selected: el.dataset.value ? el.dataset.value.split(',').filter(v => v.trim() !== '') : [],
    selectedLabels: {},
    url: el.dataset.url,
    create: el.dataset.create,
    loading: false,
    el:el,
    isSelected(id) {
      return this.selected.includes(id.toString());
    },
    toggleSelection(id, name) {
      id = id.toString();
      if (this.isSelected(id)) {
        this.selected = this.selected.filter(item => item !== id);
        delete this.selectedLabels[id];
      } else {
        if (!this.multiple) {
          // In single-select mode, replace the current selection
          this.selected = [id];
          this.selectedLabels = {};
          this.selectedLabels[id] = name;
        } else {
          // In multi-select mode, add to the current selection
          this.selected = [...this.selected, id];
          this.selectedLabels[id] = name;
        }
      }
    },
    async submit() {
      this.loading = true;
      window.location = '/products/create?name=' + encodeURIComponent(this.query);
    },
    async fetchResults() {
      try {
        const res = await fetch(this.url +"?search="+ encodeURIComponent(this.query));
        if (!res.ok) throw new Error('Failed to fetch results.');

        const response = await res.json()
        this.results = response.results
        this.exact_match = this.results.some(result => result.name === this.query);
      } catch (err) {
        console.error('Error:', err.message);
        this.results = [];
      }
    },
    init() {
      // Initialize selectedLabels with existing values if available
      if (this.selected.length > 0) {
        // If we have labels from data-label attribute
        if (el.dataset.label) {
          const labels = el.dataset.label.split(',');
          // If we have the same number of labels as selected items, use them
          if (labels.length === this.selected.length) {
            for (let i = 0; i < this.selected.length; i++) {
              this.selectedLabels[this.selected[i]] = labels[i];
            }
          } else {
            // If the counts don't match, fetch labels
            this.fetchInitialLabels();
          }
        }
        // If we need to fetch labels for initial values
        else {
          this.fetchInitialLabels();
        }

        // In single-select mode, ensure only one item is selected
        if (!this.multiple && this.selected.length > 1) {
          const firstId = this.selected[0];
          this.selected = [firstId];
          const firstLabel = this.selectedLabels[firstId];
          if (firstLabel) {
            const tempLabel = this.selectedLabels[firstId];
            this.selectedLabels = {};
            this.selectedLabels[firstId] = tempLabel;
          }
        }

        // Clear the query field when there are selected items
        this.query = '';
      }

      this.$watch('query', debounce(() => {
        if (this.query.trim() !== '') {
          this.fetchResults();
        } else {
          this.results = [];
          this.exact_match = false;
        }
      }, 300));

      this.$watch('selected', () => {
        this.$nextTick(() => {
          const select = this.el.querySelector('select');
          if (select) {
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    },

    async fetchInitialLabels() {
      if (this.selected.length === 0) return;

      try {
        const ids = this.selected.join(',');
        const res = await fetch(`${this.url}?ids=${ids}`);
        if (!res.ok) throw new Error('Failed to fetch initial labels.');

        const items = await res.json();
        items.results.forEach(item => {
          this.selectedLabels[item.id] = item.name;
        });
      } catch (err) {
        console.error('Error fetching initial labels:', err.message);
      }
    }
  };
}
